import {ChangeDetectorRef, Component, DestroyRef, EventEmitter, OnInit, Output, inject} from '@angular/core';
import {filter, Observable, of, shareReplay, tap} from 'rxjs';
import {
  dispatchDeletedItemEvent,
  dispatchSavedItemEvent,
  SearchCriteria,
  SearchResult
} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  BokkzAlgDataService,
  BokkzBtwDataService,
  BokkzBtwLogic,
  BokkzClient,
  FeatureServiceComponent,
  MenuInterface,
  SubOnderdeel,
  ValueAddedTaxEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute, Router} from '@angular/router';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-vat-list',
  templateUrl: './feature-tax-vat-list.component.html',
  styleUrls: ['./feature-tax-vat-list.component.scss'],
  imports: [SharedUiTableModule, SharedUiButtonModule, CommonModule, FeatureServiceComponent]
})
export class FeatureTaxVatListComponent implements OnInit {

  @Output() public details = new EventEmitter<string>();
  private readonly destroyRef = inject(DestroyRef);
  public readonly dataService = inject(BokkzBtwDataService);
  public readonly btwService = inject(BokkzBtwLogic);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly router = inject(Router);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected tableConfig!: TableConfiguration<any, any>;
  protected clientId = '';

  public dialogVisible = false;
  public showTable = false;
  public year = 0;
  public options: MenuInterface[] = [];
  private visibleMenuSnapshot: MenuInterface[] = [];
  private dispatchMenuTimer: ReturnType<typeof setTimeout> | null = null;
  public clientName = '';
  public vatEntity = {} as ValueAddedTaxEntity;
  public index = 0;
  public activePage: null | string = '';

  readonly valueAddedTaxEntity$: Observable<ValueAddedTaxEntity> =
    this.dataService.valueAddedTaxEntity$.pipe(
      filter((data): data is ValueAddedTaxEntity => !!data?.sstRelationId),
      tap(valueAddedTaxEntity => {
        this.applyValueAddedTaxEntity(valueAddedTaxEntity);
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      if (this.dispatchMenuTimer) {
        clearTimeout(this.dispatchMenuTimer);
        this.dispatchMenuTimer = null;
      }
    });

    this.valueAddedTaxEntity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.observeContext();
    this.observeMenuInterface();

    const currentUrl = this.router.url;
    const segments = currentUrl.split('/');
    const lastSegment = segments[segments.length - 1];
    this.activePage = lastSegment === 'ICP opgave' ? null : lastSegment;
  }

  public navigateToSubPage(subOnderdeel: SubOnderdeel): void {
    const page = subOnderdeel.pageMapping;
    const url = '/tax-vat/' + 'modules/' + this.clientId + '/' + page;
    this.activePage = page === 'ICP opgave' ? null : page;
    this.dataService.updatePeriodeIndex(subOnderdeel.value);
    this.router.navigate([url]).then(() => console.log('complete'));
  }

  private observeContext(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.clientId = params['clientId'] ?? '';
      this.loadPageData();
    });

    this.algDataService.selectedYear.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(year => {
      this.year = year;
      this.loadPageData();
    });
  }

  private observeMenuInterface(): void {
    this.dataService.options.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(menuInterface => {
      const previousVisibleMenu = this.visibleMenuSnapshot;
      const next = this.sortVisibleMenu(menuInterface);
      const hasVisibleMenuChanged = !this.areMenuListsEqual(this.visibleMenuSnapshot, next);

      if (hasVisibleMenuChanged) {
        this.options = this.cloneVisibleMenu(next);
        this.visibleMenuSnapshot = this.cloneVisibleMenu(next);
        this.cd.detectChanges();

        if (this.showTable) {
          this.scheduleVisibleMenuDispatch(previousVisibleMenu, this.visibleMenuSnapshot);
        }
      }
    });
  }

  private loadPageData(): void {
    if (!this.clientId || !this.year) {
      return;
    }

    if (this.dispatchMenuTimer) {
      clearTimeout(this.dispatchMenuTimer);
      this.dispatchMenuTimer = null;
    }

    this.showTable = false;
    this.options = [];
    this.visibleMenuSnapshot = [];

    this.bokkzClient.getValueAddedTax(this.clientId, String(this.year)).pipe(
      takeUntilDestroyed(this.destroyRef)).subscribe({
      next: valueAddedTaxEntity => {
        if (valueAddedTaxEntity.overzicht) {
          this.dataService.updateValueAddedTaxEntity(valueAddedTaxEntity);

          this.dataService.calculateLatestValueAddedTaxEntity().pipe(
            takeUntilDestroyed(this.destroyRef)
          ).subscribe({
            next: () => {
              const latestEntity = this.dataService.valueAddedTaxEntity.value;
              if (latestEntity?.overzicht) {
                this.applyValueAddedTaxEntity(latestEntity);
              }
              this.btwService.getValidation();
              this.finishPageLoad();
            },
            error: () => {
              this.finishPageLoad();
            }
          });
          return;
        }

        this.clientName = valueAddedTaxEntity.name ?? '';
        this.dataService.updateAantalValidaties(0);
        this.finishPageLoad();
      },
      error: () => {
        this.options = [];
        this.dataService.updateAantalValidaties(0);
        this.finishPageLoad();
      }
    });
  }

  private finishPageLoad(): void {
    this.createTableConfig();
    queueMicrotask(() => {
      this.showTable = true;
    });
  }

  private scheduleVisibleMenuDispatch(previous: MenuInterface[], current: MenuInterface[]): void {
    if (this.dispatchMenuTimer) {
      clearTimeout(this.dispatchMenuTimer);
    }

    const previousSnapshot = this.cloneVisibleMenu(previous);
    const currentSnapshot = this.cloneVisibleMenu(current);

    this.dispatchMenuTimer = setTimeout(() => {
      this.dispatchVisibleMenuChanges(previousSnapshot, currentSnapshot);
      this.dispatchMenuTimer = null;
    }, 25);
  }

  private dispatchVisibleMenuChanges(previous: MenuInterface[], current: MenuInterface[]): void {
    previous.forEach(item => {
      dispatchDeletedItemEvent({identifier: item.value});
    });

    setTimeout(() => {
      [...current].reverse().forEach(item => {
        dispatchSavedItemEvent({
          identifier: item.value,
          result: item as unknown as Record<number, MenuInterface>
        });
      });
    }, 0);
  }

  private sortVisibleMenu(options: MenuInterface[]): MenuInterface[] {
    return [...options]
      .filter(item => item.visible)
      .map(group => ({
        ...group,
        subonderdelen: (group.subonderdelen || [])
          .filter(sub => sub.visible)
          .sort((a, b) => a.value - b.value)
          .map(sub => ({...sub}))
      }))
      .sort((a, b) => a.value - b.value);
  }

  private cloneVisibleMenu(options: MenuInterface[]): MenuInterface[] {
    return options.map(group => ({
      ...group,
      subonderdelen: (group.subonderdelen || []).map(sub => ({...sub}))
    }));
  }

  private areMenuListsEqual(left: MenuInterface[], right: MenuInterface[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((item, index) => this.areMenuItemsEqual(item, right[index]));
  }

  private areMenuItemsEqual(left?: MenuInterface, right?: MenuInterface): boolean {
    if (!left || !right) {
      return left === right;
    }

    return left.value === right.value &&
      left.id === right.id &&
      left.label === right.label &&
      left.visible === right.visible &&
      left.pageMapping === right.pageMapping &&
      this.areSubOnderdelenEqual(left.subonderdelen, right.subonderdelen);
  }

  private areSubOnderdelenEqual(left?: MenuInterface['subonderdelen'], right?: MenuInterface['subonderdelen']): boolean {
    if (!left?.length && !right?.length) {
      return true;
    }

    if (!left || !right || left.length !== right.length) {
      return false;
    }

    return left.every((item, index) => {
      const other = right[index];
      return item.label === other.label &&
        item.pageMapping === other.pageMapping &&
        item.visible === other.visible &&
        item.value === other.value;
    });
  }

  private applyValueAddedTaxEntity(valueAddedTaxEntity: ValueAddedTaxEntity): void {
    this.vatEntity = valueAddedTaxEntity;
    this.clientName = valueAddedTaxEntity.name ?? '';
  }

  private createTableConfig(): void {
    const criteria = new SearchCriteria();

    this.tableConfig = TableConfiguration.create({
      identifier: 'value',
      options: {
        useSearch: false,
        useSort: true
      },
      criteria,

      search: () => of(new SearchResult(this.options, this.options.length)),

      details: (entity) => {
        if (entity) {
          this.details.next(entity.label);
        }
      },

      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, MenuInterface>;
      },

      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2024
          ? 'Module niet beschikbaar'
          : $localize`Geen aangifte gevonden voor ` + this.year,
        subText: this.year < 2024
          ? 'Deze aangifte omzetbelasting wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
          : 'Maak een nieuwe aangifte aan en start direct met invullen.',
        buttonText: this.year < 2024 ? undefined : `Maak nieuwe aangifte`,
        buttonConfig: this.year < 2024 ? undefined : ButtonConfiguration.create({
          onClick: () => {
            return new Observable((subscriber) => {
              setTimeout(() => {
                subscriber.next((this.dialogVisible = true));
                subscriber.complete();
              }, 50);
            });
          },
        }),
      }),
    });
  }
}
